import { useState, useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
    MarkEmailUnread,
    MarkEmailRead,
    AccessTime
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { createInvitations, getAllInvitations } from '../../../../../../app/store/slices/event/invitation';
import { EMAIL_REGEX, INDIA_PHONE_REGEX } from '../../../../../../utils/validations';
import { errorToast, successToastTopRight } from '../../../../../../utils/toasts/toasts';

export default function useAddInvitations() {
    const dispatch = useDispatch();

    const { invitations } = useSelector((state) => state.invitation);
    const { eventData } = useSelector(state => state.event);
    const invitationSettings = useSelector(state => state.adminSettings.invitation);

    const [openInvitationForm, setOpenInvitationForm] = useState(false);
    const [openExcelUploadModal, setOpenExcelUploadModal] = useState(false);

    const [file, setFile] = useState(null);  // to hold the uploaded file (File Object)
    const [excelFileJsonData, setExcelFileJsonData] = useState(null);

    const [excelFileError, setExcelFileError] = useState(false);
    const [fileUploadStatus, setFileUploadStatus] = useState("idle") // idle, uploading, success, error, loading
    const [progress, setProgress] = useState(0)
    const [loading, setLoading] = useState(false);


    const fileInputRef = useRef(null)

    const eventId = eventData?.eventId;

    const initialValues = { invitations: [] };

    /**
     * Yup validation schema for the invitation form
     */
    const validationSchema = Yup.object({
        invitations: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required('Name is required'),
                email: Yup.string()
                    .required('Email is required')
                    .matches(EMAIL_REGEX, 'Email is not valid'),
                phone: Yup.string()
                    .required('Phone number is required')
                    .matches(INDIA_PHONE_REGEX, 'Phone number is not valid')
            })
        )
    });


    /**
     * useFormik hook to manage the invitation form state and validation
     * Custom validation to check for duplicate invitations against existing ones
     * onSubmit function to dispatch the createInvitations action
     * Handles loading state and success/error toasts
     */
    const formik = useFormik({
        initialValues,
        validationSchema,
        validate: (values) => {
            const errors = { invitations: [] };
            let hasErrors = false;


            /**
             * Check if there are existing invitations to compare against
             * If there are no existing invitations, skip the duplicate check
             */
            if (invitations.length > 0) {

                /**
                 * Iterate over each invitation in the form values
                 */
                values.invitations.forEach((invite, index) => {
                    const existingInvitationMatches = invitations.filter(
                        person =>
                            person.email.toLowerCase() === invite.email.toLowerCase() ||
                            person.phone === invite.phone
                    );


                    /**
                     * If matches are found, set the appropriate error messages
                     */
                    existingInvitationMatches.forEach((existingInvitation) => {
                        if (!errors.invitations[index]) errors.invitations[index] = {};

                        // If a match is found for email
                        if (existingInvitation.email.toLowerCase() === invite.email.toLowerCase()) {
                            errors.invitations[index].email = 'Invitation already exists with this email';
                            hasErrors = true;
                        }

                        // If a match is found for phone
                        if (existingInvitation.phone === invite.phone) {
                            errors.invitations[index].phone = 'Invitation already exists with this phone';
                            hasErrors = true;
                        }
                    });
                });
            }


            /**
             * Check email and phone duplication in form values itself
             */
            values.invitations.forEach((invite, index) => {
                values.invitations.forEach((otherInvite, otherIndex) => {
                    if (index !== otherIndex) {
                        if (
                            invite.email &&
                            otherInvite.email &&
                            invite.email.toLowerCase() === otherInvite.email.toLowerCase()
                        ) {
                            if (!errors.invitations[index]) errors.invitations[index] = {};
                            errors.invitations[index].email =
                                "Cannot repeat same email";
                            hasErrors = true;
                        }

                        if (
                            invite.phone &&
                            otherInvite.phone &&
                            invite.phone === otherInvite.phone
                        ) {
                            if (!errors.invitations[index]) errors.invitations[index] = {};
                            errors.invitations[index].phone =
                                "Cannot repeat same phone number";
                            hasErrors = true;
                        }
                    }
                });
            });


            /**
             * Set the errors object only if there are any errors found
             */
            return hasErrors ? errors : {};
        },
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const payload = {
                    type: 'attendee',
                    invites: values.invitations.map(invite => ({
                        email: invite.email,
                        name: invite.name,
                        phone: invite.phone
                    }))
                };

                await dispatch(createInvitations({ eventId, payload })).unwrap();

                successToastTopRight("Invitations sent successfully");

                formik.resetForm();
                setOpenInvitationForm(false);
            } catch (error) {
                // console.error("Error sending invitations -> ", error);
                errorToast("Failed to send invitations");
            } finally {
                setLoading(false);
            }
        }
    });


    /**
     * useEffect to fetch existing invitations when the component mounts
     * or when the eventId changes
     * Sets loading state while fetching and handles errors
     */
    useEffect(() => {
        const fetchAllInvitations = async () => {
            if (eventId && (!invitations || invitations.length === 0)) {
                try {
                    setLoading(true);
                    await dispatch(getAllInvitations(eventId)).unwrap();
                } catch (error) {
                    errorToast(error?.message || "Failed to fetch invitations");
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchAllInvitations();
    }, [eventId, dispatch]);


    /**
     * @function to add a new empty invitation to the form
     * Opens the invitation form if it is not already open
     * Updates the formik form values with a new empty invitation
     */
    function addInvitation() {
        if (!openInvitationForm) setOpenInvitationForm(true);
        formik.setFieldValue('invitations', [
            ...formik.values.invitations,
            { name: '', email: '', phone: '' }
        ]);
    }


    /**
     * @function to delete an invitation at a specific index
     * Updates the formik form values and closes the form if no invitations remain
     * 
     * @param {number} index - The index of the invitation to delete
     */
    function deleteInvitation(index) {
        const updatedInvitations = formik.values.invitations.filter((_, i) => i !== index);
        formik.setFieldValue('invitations', updatedInvitations);

        if (updatedInvitations.length === 0) {
            setOpenInvitationForm(false);
        }
    }


    /**
     * @function to cancel the entire invitation form
     * Resets the formik form and closes the invitation form
     * 
     * @returns {boolean} false to prevent default form submission behavior
     */
    function cancelAllForm() {
        formik.resetForm();
        setOpenInvitationForm(false);
    }


    /**
     * useEffect to initialize the form with one empty invitation
     * when the invitation form is opened and there are no existing invitations
     */
    useEffect(() => {
        if (openInvitationForm && (!invitations || invitations.length === 0)) {

            const newMember = {
                name: '',
                email: '',
                phone: '',
            };

            if (formik.values.invitations.length === 0) {
                formik.setFieldValue('invitations', [newMember]);
            }
        }
    }, [openInvitationForm]);


    /**
     * useMemo to calculate the maximum invitation limit and remaining invitations
     * based on the invitation settings and current invitations
     * Updates whenever invitationSettings, invitations, or formik values change
     * 
     * @return {Object} An object containing limit, sent, remaining, and counter values
     */
    const maxInviteLimit = useMemo(() => {
        return {
            limit: invitationSettings?.maxInvitationLimit,
            sent: (invitations?.length || 0),
            remaining: invitationSettings?.maxInvitationLimit - (invitations?.length || 0),
            counter: (invitations?.length || 0) + (formik.values.invitations?.length || 0)
        }
    }, [invitationSettings, invitations, formik.values.invitations]);


    /**
     * @function to remove the uploaded Excel file and reset related states
     * Resets the file input value, excel file states, and upload status
     */
    function removeUploadedFile() {
        if (fileInputRef?.current) fileInputRef.current.value = null;
        setExcelFileError(false);
        setFile(null);
        setFileUploadStatus("idle");
        setExcelFileJsonData(null);
    }


    /**
     * @function to check if the uploaded file is an Excel file based on its extension
     * @param {File} file - The uploaded file
     * @returns {boolean} - True if the file is an Excel file, false otherwise
     */
    function isExcelFile(file) {
        const fileName = file.name.toLowerCase();
        return fileName.endsWith(".xls") || fileName.endsWith(".xlsx");
    }


    /**
     * @function to handle the file input change event
     * Validates the file type and reads the Excel file using FileReader and xlsx library
     * Parses the Excel data into JSON format and performs validation checks
     * 
     * @param {Event} e - The file input change event
     */
    function handleFileInput(e) {
        const selectedFile = e.target.files[0]

        /**
         * If no file is selected, return early
         */
        if (!selectedFile) return;

        /**
         * Check if the selected file is an Excel file
         * If not, show an error toast and reset relevant states
         */
        if (!isExcelFile(selectedFile)) {
            errorToast("Please upload a valid Excel file (.xls or .xlsx).")
            setFileUploadStatus("error");
            setExcelFileError("Invalid file type");
            setFile(null);
            return;
        }

        setFile(selectedFile);
        // setFileUploadStatus("success")

        const reader = new FileReader();

        /**
         * On file load, parse the Excel data
         */
        reader.onload = async (e) => {
            try {
                setFileUploadStatus("uploading");
                setProgress(0);

                setProgress(0)

                // Simulate upload progress
                for (let i = 0; i <= 100; i += 10) {
                    await new Promise((resolve) => setTimeout(resolve, 100))
                    setProgress(i)
                }

                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];

                /**
                 * Raw data of excel file in array of arrays format
                 */
                const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                /**
                 * By converting to json header row will be keys and rest will be values.
                 * Empty cells will be filled with empty string automatically with defval option.
                 */
                let jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

                /**
                 * Validation checks on the raw data
                 */
                const validationChecks = [
                    checkFileNotEmpty,
                    checkColumnNames,
                    checkHasRecords
                ];

                /**
                 * Run each validation check and handle errors
                 */
                for (let check of validationChecks) {
                    const result = check(rawData, selectedFile);
                    if (!result.valid) {
                        setExcelFileError(result.error);
                        setFileUploadStatus("error");
                        return;
                    }
                }

                /**
                 * Dynamically remove invitation data from file if it exceeds the remaining limit
                 */
                if (jsonData.length > maxInviteLimit.remaining) {
                    jsonData = jsonData.slice(0, maxInviteLimit.remaining);
                }

                setExcelFileJsonData(jsonData);
                setExcelFileError(false);
                setFileUploadStatus("success");

            } catch (err) {
                setExcelFileError("Failed to parse the Excel file or file is corrupted.");
                setFileUploadStatus("error");
            }
        };

        reader.readAsArrayBuffer(selectedFile);

    }


    /**
     * @function to submit the parsed Excel data to the form
     * Sets the submitExcelData state to true to indicate submission
     * Closes the Excel upload modal and opens the invitation form
     * Merges the parsed Excel data into the existing formik invitations
     * Trims whitespace from name, email, and phone fields
     */
    function submitExcelDataToForm() {
        if (excelFileJsonData) {
            setOpenExcelUploadModal(false)
            setOpenInvitationForm(true);
            formik.setFieldValue('invitations', [
                ...formik.values.invitations,
                ...excelFileJsonData.map(record => ({
                    name: record.name ? String(record.name).trim() : '',
                    email: record.email ? String(record.email).trim() : '',
                    phone: record.phone ? String(record.phone).trim() : '',
                }))
            ]);

            // Set loading state for 4 seconds
            setFileUploadStatus("loading");
            setTimeout(() => {
                setFileUploadStatus("idle");
            }, 4000);
        }
    }

    // console.log("formik values -> ", formik.values)
    // console.log("Final parsed json data -> ", excelFileJsonData);
    // console.log("Excel file error -> ", excelFileError);

    /* ------------------- Validation Checks While User Uploading Excel File ------------------- */

    /**
     * Check 1: 
     * File should not be empty
     * @param {Array} rawData - The parsed rawData data from the Excel file
     * @returns {Object} - An object with 'valid' boolean and 'error' message if invalid
     */
    function checkFileNotEmpty(rawData) {
        if (!rawData || rawData.length === 0) {
            return { valid: false, error: "File is empty. Please upload a valid file." };
        }
        return { valid: true };
    }


    /**
     * Check 2: 
     * Must have expected columns in expected order -> Name, Email, Phone or name, email, phone
     * @param {Array} rawData - The parsed rawData data from the Excel file
     * @returns {Object} - An object with 'valid' boolean and 'error' message if invalid
     */
    function checkColumnNames(rawData) {
        const expectedColumns = ["name", "email", "phone"];
        const firstRow = rawData[0];

        if (!firstRow || firstRow.length === 0) {
            return { valid: false, error: "Missing header row in file." };
        }

        const columnNames = firstRow.map(col => String(col).toLowerCase().trim());

        const isValid =
            columnNames.length === expectedColumns.length &&
            columnNames.every((col, index) => col === expectedColumns[index]);

        if (!isValid) {
            return { valid: false, error: "Missing header row in file." };
        }

        return { valid: true };
    }


    /**
     * Check 3:
     * File should have number of data excluding header. (file should not be empty with header)
     * @param {Array} rawData - The parsed rawData data from the Excel file
     * @returns {Object} - An object with 'valid' boolean and 'error' message if invalid
     */
    function checkHasRecords(rawData) {
        if (rawData.length <= 1) {
            return { valid: false, error: "No Records found in file" };
        }
        return { valid: true };
    }


    /**
     * Analytics Cards Data
     */
    const analyticsCards = [
        {
            title: 'Maximum Invites',
            value: maxInviteLimit?.limit,
            valueColor: 'var(--quinary-secondary-color)',
            icon: <MarkEmailUnread sx={{ color: 'var(--quinary-secondary-color)' }} />,
            iconBgColor: 'var(--quinary-light-bg-color-2)'
        },
        {
            title: 'Invites Sent',
            value: maxInviteLimit?.sent,
            valueColor: 'var(--success-secondary-color)',
            icon: <MarkEmailRead sx={{ color: 'var(--success-secondary-color)' }} />,
            iconBgColor: 'var(--success-light-bg-color-2)'
        },
        {
            title: 'Available Invites',
            value: maxInviteLimit?.remaining,
            valueColor: 'var(--warning-main-color)',
            icon: <AccessTime sx={{ color: 'var(--warning-main-color)' }} />,
            iconBgColor: 'var(--warning-light-bg-color)'
        }
    ];


    /**
     * Return all necessary states and functions for managing invitations
     */
    return {
        formik,
        addInvitation,
        deleteInvitation,
        cancelAllForm,
        loading,
        openInvitationForm,
        setOpenInvitationForm,
        maxInviteLimit,
        openExcelUploadModal,
        setOpenExcelUploadModal,
        analyticsCards,
        fileInputRef,
        excelFileError,
        file,
        removeUploadedFile,
        handleFileInput,
        fileUploadStatus,
        submitExcelDataToForm,
        progress
    };
}

/**
 * TODO: Check when user upload excel file
 * 1. file should be excel [done]
 * 2. file should have expected columns in expected order -> Name, Email, Phone or name, email, phone [done]
 * 3. file should have number of data excluding header. (file should not be empty) [done]
 * 4. file should have maximum 30 invite list excluding header. so total rows will be 31 but only check against invite data list which is 30 [done]
 * 5. If there are existing invitations, check for duplicates against existing invitations. if there are then show error. [handle in form validation] [done]
 * 6. if any of field does not match with expected validation then replace with empty string and upload data in form. [done]
 * 7. if any of the field is empty then keep it empty. it will handle on form submission. [done]
 */

/**
 * Make a MERN Stack Project which has following features:
 * 1. Frontend (Vite react js)
 *    -> User make request to generate a pdf with some predefined data for testing point of view
 *    -> as response user will get a pdf file from backend and directly download pdf in user browser
 * 
 * 2. Backend (Node js with express js)
 *    -> Make a route for user where user can make request with payload
 *    -> Use puppeteer to generate html page and then a pdf with the payload data and some predefined styling
 *    -> send the pdf as response to user.
 *  
 *  We dont need database to store the pdf. user can request for pdf whenver they want pdf.
 * 
 * define all the commands to setup the frontend and backend. i have already setup the vite react js codebase. jsut tell me what are the file should i add in frontend
 * use module js in backend instead of common.
 * make changes in frontend that can indeitfy pdf response from backend and download it and also preview of it for testing point of you.
 * 
 * give me code as snippets.
 * use A4 size paper for pdf
 * 
 */